import React, { useCallback, useState } from 'react';
import { Button } from 'is24-corecss';
import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import SchlüsselnotdienstJpg from './images/key_emergency.png';
import SchlüsselnotdienstWebp from './images/key_emergency.webp';
import SchlüsselnotdienstMobileJpg from './images/key_emergency_mobile.jpg';
import SchlüsselnotdienstMobileWebp from './images/key_emergency_mobile.webp';
import { EntitlementType } from '../../../../../@types/entitlement';
import { useGetHasEntitlement } from '../../../../../customHooks/useGetHasEntitlement.hook';
import { ProductsStatusKey } from '../../../../../@types/productsStatus';
import SchlüsselnotdienstSubmitForm from './components/SchlüsselnotdienstSubmitForm';
import HowItWorksTab from './components/HowItWorksTab';
import OverviewTab from './components/OverviewTab';
import { OverviewTrackingEvents } from '../../../../../tracking/trackingEvents';
import { useHasMeinPlus } from '../../../../../customHooks/useHasMeinPlus.hook';
import { report } from '../../../../../tracking/tracking';
import { ProfileBenefitType, ProfileBenefitsStatusResponse } from '../../../../../@types/productBenefits';
import {
  useGetProductBenefitsPersonalData,
  useProductBenefitsGrantBenefit,
  useProductBenefitsStartContract,
} from '../../../../../apiHooks/productBenefits.hook';
import { useProductStatus } from '../../../../../customHooks/useProductStatus.hook';
import { useIntl } from '@/i18n';

const BASIC_URL =
  '/warenkorb/wohnenplus?cmp_id=10-052071&cmp_name=premium_profile_living&cmp_position=residential_benefits&cmp_creative=legaladvice_cta';

export const Schlüsselnotdienst = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const [showForm, setShowForm] = useState(false);
  const handleCloseForm = () => setShowForm(false);
  const isEntitled = useGetHasEntitlement(EntitlementType.KEY_AND_LEGAL_SERVICE);
  const { personalData: profile, initializing } = useGetProductBenefitsPersonalData();
  const product = useProductStatus(ProductsStatusKey.ProfileBenefits);
  const keyEmergencyService = (product as ProfileBenefitsStatusResponse)?.keyEmergencyService;

  const isMeinPlus = useHasMeinPlus();
  const { mutate, isLoading } = useProductBenefitsGrantBenefit();
  const { mutate: mutateProductBenefitsStart, isLoading: isLoadingProductBenefitsStart } =
    useProductBenefitsStartContract();

  const showLoading = isLoading || isLoadingProductBenefitsStart || initializing;

  const handleOpenForm = useCallback(async () => {
    await report(OverviewTrackingEvents.KEY_EMERGENCY.FORM_VISITED, isMeinPlus);
    setShowForm(true);
  }, [setShowForm, report]);

  const handleSubmitCallback = useCallback(
    async (values: any) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { policyNumber, ...valuesToSubmit } = values;
        await mutateProductBenefitsStart({
          benefitType: ProfileBenefitType.KEY_EMERGENCY_SERVICE,
          formData: valuesToSubmit,
        });
      } catch (e) {
        await mutate(ProfileBenefitType.KEY_EMERGENCY_SERVICE);
        // TODO: post-migration handle error catch with alert
      } finally {
        await report(OverviewTrackingEvents.KEY_EMERGENCY.FUNNEL_SUBMIT, isMeinPlus);
        setShowForm(false);
      }
    },
    [isMeinPlus, profile, keyEmergencyService],
  );

  const renderButtonArea = useCallback(() => {
    if (keyEmergencyService || showForm) {
      return null;
    }

    return (
      <div className='align-center'>
        {isEntitled ? (
          <Button onClick={handleOpenForm} appearance='secondary'>
            {t({
              id: 'benefits.keyEmergency.modal.activeButton',
              defaultMessage: 'Schlüsselnotdienst aktivieren',
            })}
          </Button>
        ) : (
          <a className={'button-secondary'} href={BASIC_URL}>
            {t({ id: 'benefits.keyEmergency.modal.linkButton', defaultMessage: 'WohnenPlus entdecken' })}
          </a>
        )}
      </div>
    );
  }, [showForm, isEntitled, keyEmergencyService]);

  return (
    <Modal
      title={t({ id: 'benefits.keyEmergency.modal.headline', defaultMessage: 'Schlüsselnotdienst' })}
      isVisible={isVisible}
      image={SchlüsselnotdienstJpg}
      imageWebp={SchlüsselnotdienstWebp}
      imageMobile={SchlüsselnotdienstMobileJpg}
      imageMobileWebp={SchlüsselnotdienstMobileWebp}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.keyEmergency.modal.image',
        defaultMessage: 'Woman using mortgage manager',
      })}
      tabsContent={[
        <OverviewTab
          key='benefits.keyEmergency.modal.overview'
          isEntitled={isEntitled}
          isKeyEmergencyService={keyEmergencyService}
        />,
        <HowItWorksTab key='benefits.keyEmergency.modal.howItWorks' />,
      ]}
      formData={
        showForm ? (
          <SchlüsselnotdienstSubmitForm
            close={handleCloseForm}
            onSubmit={handleSubmitCallback}
            isLoading={showLoading}
          />
        ) : null
      }
      buttonArea={renderButtonArea()}
      benefit={Benefit.KEY_EMERGENCY}
      onClose={onClose}
    />
  );
};
